/**
 * canvas-engine/renderer/CanvasPage.jsx
 *
 * OWN shared stacking renderer for the Canvas Engine — the SINGLE component that
 * renders an ordered list of Canvas_Sections as one continuous page, used by
 * BOTH the in-process builder Preview and the published Reader. Because the same
 * component performs the same stacking, full-bleed, and contained treatment in
 * both modes, Preview ↔ Reader parity is structural (by construction), not a
 * coincidence of two parallel implementations. (Req 4.x, 5.1, 5.2)
 *
 * ZERO REUSE: imports NOTHING from `admin-console-ui/src/modules/template-builder/`
 * or `academy-ui/src/template/`. It composes ONLY its own canvas-engine siblings
 * (the unified `CanvasRenderer`, the section error boundary, the canvas-model
 * normalizer, and the frame-height fallback constant) plus React.
 *
 * Responsibilities:
 *   - Stack the ordered sections in a single vertical flow with EXACTLY 0 gap,
 *     0 margin, 0 padding, and 0 border between adjacent section bands, so each
 *     section's bottom edge is flush with the next section's top edge. No
 *     separators, drop-shadows, or framing beyond each section's own background.
 *     (Req 4.1, 4.5)
 *   - Render each section as a full-width BAND wrapping a single `CanvasRenderer`:
 *       • Full_Bleed_Section (`canvas.fullBleed === true`): the band paints the
 *         section background edge-to-edge at 100% width with 0 horizontal inset,
 *         and the inner frame sits with 0 inset. (Req 4.3)
 *       • Contained_Section (otherwise): the band is transparent and the inner
 *         content frame is horizontally centered with `contentInset` applied as
 *         left/right padding and constrained to `contentMaxWidth`. (Req 4.4)
 *     `CanvasRenderer` already centers its fixed-width frame with `margin: 0
 *     auto`; CanvasPage only owns the band (full-bleed vs contained + flush
 *     stacking) and never alters `CanvasRenderer`'s own output.
 *   - Honor each section's full Canvas_Model height with NO max-height, NO
 *     overflow clipping, and NO collapse. A missing/invalid height falls back to
 *     `DEFAULT_FRAME_HEIGHT`, and in builder mode a small inline note flags the
 *     band as "section height unavailable" without breaking the others. (Req
 *     4.2, 4.9)
 *   - Delegate each section to the existing `CanvasRenderer` (so element
 *     geometry, content, and the builder-only selection affordances on element
 *     wrappers are unchanged) wrapped in `CanvasSectionErrorBoundary` so one bad
 *     section cannot abort the page. CanvasPage adds NO builder affordances of
 *     its own. (Req 5.5, 4.6)
 *   - Empty/zero-section list renders an empty container with NO placeholder
 *     framing and NO error indication in reader mode (the builder shell shows its
 *     own add-section affordance). (Req 4.8)
 *   - In builder mode, clicking the page background (a band/container, never an
 *     element) calls `onClearSelection` if provided. `CanvasRenderer`'s element
 *     click handlers call `event.stopPropagation()`, so element clicks never
 *     bubble here — only genuine background clicks reach the container. (Req 4.7)
 *
 * Pure presentational component: no data fetching. `data`/`theme` are passed
 * straight through to each `CanvasRenderer` (for data-bound elements).
 */

import React from 'react';
import { normalizeCanvas } from '../model/canvasModel';
import { DEFAULT_FRAME_HEIGHT } from '../model/schema';
import CanvasRenderer from './CanvasRenderer';
import CanvasSectionErrorBoundary from './CanvasSectionErrorBoundary';

// ── Small pure helpers ───────────────────────────────────────────────────────

/** A positive, finite height or the defined fallback. */
function resolveHeight(canvas) {
  const h = canvas && canvas.height;
  if (Number.isFinite(h) && h > 0) {
    return { height: h, valid: true };
  }
  return { height: DEFAULT_FRAME_HEIGHT, valid: false };
}

/** Resolve a stable key for a section band. */
function resolveSectionKey(section, index) {
  if (section && typeof section === 'object') {
    if (typeof section.sectionId === 'string' && section.sectionId.length > 0) return section.sectionId;
    if (typeof section.id === 'string' && section.id.length > 0) return section.id;
  }
  return `section-${index}`;
}

function resolveSectionKind(section, canvas) {
  if (section && typeof section === 'object') {
    const explicit = typeof section.type === 'string' ? section.type.toLowerCase() : '';
    if (explicit) return explicit;
  }
  const source = canvas && canvas.source && typeof canvas.source === 'object' ? canvas.source : {};
  return typeof source.type === 'string' ? source.type.toLowerCase() : '';
}

// ── Static styles ─────────────────────────────────────────────────────────────

/**
 * Outer page container: a single vertical flow. No gap/margin/padding/border so
 * sections stack flush. `width: 100%` lets full-bleed bands span the available
 * content width. No `overflow` so nothing is clipped. (Req 4.1, 4.2, 4.5)
 */
const PAGE_STYLE = Object.freeze({
  display: 'block',
  width: '100%',
  margin: 0,
  padding: 0,
  border: 0,
});

/** A small builder-only note shown when a band's height was unavailable. */
const HEIGHT_NOTE_STYLE = Object.freeze({
  position: 'absolute',
  top: 8,
  left: 8,
  zIndex: 2147483647,
  padding: '2px 8px',
  background: 'rgba(220,38,38,.9)',
  color: '#fff',
  fontSize: 11,
  lineHeight: 1.4,
  borderRadius: 6,
  pointerEvents: 'none',
});

/**
 * Build the band style for one section. The band is always full-width with 0
 * surrounding spacing (flush stacking). Full-bleed bands paint the section
 * background edge-to-edge with 0 horizontal inset; contained bands stay
 * transparent and center their content with `contentInset` padding, capped at
 * `contentMaxWidth`. `minHeight` guarantees the band accommodates the full
 * canvas height with no collapse, while the absence of `maxHeight`/`overflow`
 * guarantees no cap and no clipping. (Req 4.2, 4.3, 4.4, 4.5)
 */
function buildBandStyle(canvas, height, { isBuilder, sectionKind } = {}) {
  const base = {
    position: 'relative',
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: 0,
    border: 0,
  };

  if (isBuilder) base.minHeight = height;

  if (sectionKind === 'header') {
    base.position = 'sticky';
    base.top = 0;
    base.zIndex = 2147483000;
    base.isolation = 'isolate';
  }

  if (canvas.fullBleed) {
    // Full-bleed: paint the section background edge-to-edge, 0 horizontal inset.
    base.background = typeof canvas.background === 'string' ? canvas.background : 'transparent';
    return base;
  }

  // Contained: transparent band that centers its inner content frame.
  base.background = 'transparent';
  base.display = 'flex';
  base.justifyContent = 'center';
  return base;
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * Render an ordered list of Canvas_Sections as one continuous page.
 *
 * @param {object}   props
 * @param {Array}    [props.sections]          ordered canvas sections ({ content: { canvas } })
 * @param {('reader'|'builder')} [props.mode]  render mode (default 'reader')
 * @param {*}        [props.data]              pass-through data for data-bound elements
 * @param {object}   [props.theme]             pass-through theme for elements
 * @param {string}   [props.selectedElementId] builder-only selected element id
 * @param {function} [props.onSelectElement]   builder-only selection callback
 * @param {function} [props.onClearSelection]  builder-only background-click deselect callback
 * @param {function} [props.onChangeElementFrame] builder-only frame-change callback
 */
export default function CanvasPage({
  sections,
  mode = 'reader',
  data,
  theme,
  selectedElementId,
  onSelectElement,
  onClearSelection,
  onChangeElementFrame,
}) {
  const isBuilder = mode === 'builder';
  const list = Array.isArray(sections) ? sections : [];

  // Background-click deselect (builder only). Element clicks stopPropagation in
  // CanvasRenderer, so only genuine background clicks reach this handler.
  const handleBackgroundClick = isBuilder && typeof onClearSelection === 'function'
    ? () => onClearSelection()
    : undefined;

  // Empty list: an empty container with no placeholder framing and no error.
  if (list.length === 0) {
    return <div className="ce-page ce-page--empty" style={PAGE_STYLE} onClick={handleBackgroundClick} />;
  }

  return (
    <div className="ce-page" style={PAGE_STYLE} onClick={handleBackgroundClick}>
      {list.map((section, index) => {
        const canvas = normalizeCanvas(section && section.content ? section.content.canvas : undefined);
        const { height, valid } = resolveHeight(canvas);
        const sectionKind = resolveSectionKind(section, canvas);
        const bandStyle = buildBandStyle(canvas, height, { isBuilder, sectionKind });
        const key = resolveSectionKey(section, index);

        // The CanvasRenderer (delegated geometry + affordances, unchanged).
        const rendered = (
          <CanvasSectionErrorBoundary label="Section unavailable">
            <CanvasRenderer
              section={section}
              mode={mode}
              data={data}
              theme={theme}
              selectedElementId={selectedElementId}
              onSelectElement={onSelectElement}
              onChangeElementFrame={onChangeElementFrame}
            />
          </CanvasSectionErrorBoundary>
        );

        // Full-bleed: the renderer's centered frame sits directly in the band.
        // Contained: wrap the renderer so the content frame is centered, inset
        // by contentInset, and capped at contentMaxWidth.
        const body = canvas.fullBleed ? rendered : (
          <div
            className="ce-page__content"
            style={{
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: canvas.contentMaxWidth,
              paddingLeft: canvas.contentInset,
              paddingRight: canvas.contentInset,
            }}
          >
            {rendered}
          </div>
        );

        return (
          <div
            key={key}
            className={`ce-page__band${canvas.fullBleed ? ' ce-page__band--full-bleed' : ' ce-page__band--contained'}`}
            data-reader-section="true"
            data-reader-section-id={key}
            data-reader-section-kind={sectionKind || undefined}
            style={bandStyle}
          >
            {isBuilder && !valid ? (
              <div className="ce-page__height-note" style={HEIGHT_NOTE_STYLE} role="note">
                Section height unavailable
              </div>
            ) : null}
            {body}
          </div>
        );
      })}
    </div>
  );
}
